import React, { useState } from 'react';
import { colors, radius, fonts } from '../theme';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  category: string;
  badge?: string;
}

const products: Product[] = [
  {
    id: 'wine1', name: 'Châteauneuf-du-Pape', description: 'Grand cru rouge, notes épicées et fruitées. Parfait avec nos plats thaï.',
    price: 48, image: 'prod_wine1.jpg', category: 'vins', badge: '⭐ Best-seller',
  },
  {
    id: 'wine2', name: 'Champagne Prestige', description: 'Brut millésimé, bulles fines et arômes d\'agrumes. Célébrez en beauté.',
    price: 65, image: 'prod_wine2.jpg', category: 'vins',
  },
  {
    id: 'thai1', name: 'Pad Thai Premium', description: 'Nouilles sautées aux crevettes tiger, cacahuètes et lime. Notre classique réinventé.',
    price: 22, image: 'prod_thai1.jpg', category: 'plats', badge: '🔥 Populaire',
  },
  {
    id: 'thai2', name: 'Tom Yum Goong', description: 'Soupe épicée aux crevettes géantes, citronnelle et champignons. Feu thaï.',
    price: 18, image: 'prod_thai2.jpg', category: 'plats',
  },
  {
    id: 'thai3', name: 'Curry Vert Thaï', description: 'Curry vert crémeux au lait de coco, basilic thaï et légumes croquants.',
    price: 20, image: 'prod_thai3.jpg', category: 'plats',
  },
  {
    id: 'caraibe1', name: 'Homard Grillé Caraïbes', description: 'Homard local grillé au beurre citronné, légumes de saison et fleurs comestibles.',
    price: 45, image: 'prod_caraibe1.jpg', category: 'plats', badge: '🦞 Signature',
  },
  {
    id: 'caraibe2', name: 'Rhum Ambré Saint-Martin', description: 'Rhum vieux 8 ans, notes de vanille et caramel. Dégustation pure ou cocktail.',
    price: 14, image: 'prod_caraibe2.jpg', category: 'vins',
  },
  {
    id: 'dessert1', name: 'Mango Sticky Rice', description: 'Riz au lait de coco, mangue fraîche et feuille d\'or. Dessert roi du Siam.',
    price: 14, image: 'prod_dessert1.jpg', category: 'desserts',
  },
  {
    id: 'cocktail1', name: 'Cocktail Blue Lagoon', description: 'Curaçao bleu, rhum blanc, ananas et orchidée. Pure tropicales.',
    price: 16, image: 'prod_cocktail1.jpg', category: 'cocktails', badge: '🍹 Chef',
  },
  {
    id: 'platter1', name: 'Tapas Mix Le Siam', description: 'Assortiment spring rolls, satay, tempura crevettes et sauces. Pour deux.',
    price: 32, image: 'prod_platter1.jpg', category: 'plats',
  },
  {
    id: 'acc1', name: 'T-Shirt Le Siam', description: 'T-shirt premium 100% coton bio, logo Le Siam doré sur fond noir. Disponible S/M/L/XL.',
    price: 35, image: 'prod_accessoires1.jpg', category: 'accessoires', badge: '🆕 Nouveau',
  },
  {
    id: 'acc2', name: 'Casquette Le Siam', description: 'Casquette brodée logo doré, ajustable. Style tropical premium.',
    price: 28, image: 'prod_accessoires2.jpg', category: 'accessoires',
  },
  {
    id: 'acc3', name: 'Tote Bag Premium', description: 'Sac tote en coton épais avec logo Le Siam. Parfait pour la plage ou les courses.',
    price: 22, image: 'prod_accessoires3.jpg', category: 'accessoires',
  },
  {
    id: 'acc4', name: 'Mug Signature Le Siam', description: 'Mug céramique doré avec logo gravé. Idéal pour votre café du matin.',
    price: 18, image: 'prod_accessoires4.jpg', category: 'accessoires',
  },
];

const categories = [
  { key: 'all', label: 'Tout', emoji: '✨' },
  { key: 'vins', label: 'Vins & Spiritueux', emoji: '🍷' },
  { key: 'plats', label: 'Plats', emoji: '🍜' },
  { key: 'desserts', label: 'Desserts', emoji: '🍮' },
  { key: 'cocktails', label: 'Cocktails', emoji: '🍹' },
  { key: 'accessoires', label: 'Accessoires', emoji: '👕' },
];

interface CartItem { product: Product; qty: number; }

export function ShopPage() {
  const [filter, setFilter] = useState('all');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [addedId, setAddedId] = useState<string | null>(null);

  const filtered = filter === 'all' ? products : products.filter(p => p.category === filter);
  const cartTotal = cart.reduce((s, c) => s + c.product.price * c.qty, 0);
  const cartCount = cart.reduce((s, c) => s + c.qty, 0);

  const addToCart = (p: Product) => {
    setCart(prev => {
      const existing = prev.find(c => c.product.id === p.id);
      if (existing) return prev.map(c => c.product.id === p.id ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, { product: p, qty: 1 }];
    });
    setAddedId(p.id);
    setTimeout(() => setAddedId(null), 1200);
  };

  const removeFromCart = (id: string) => setCart(prev => prev.filter(c => c.product.id !== id));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: colors.gold, margin: 0 }}>🛒 Boutique</h1>
        <button
          onClick={() => setShowCart(!showCart)}
          style={{
            position: 'relative', padding: '10px 20px', borderRadius: 24,
            background: 'rgba(201,169,110,0.15)', border: '1px solid rgba(201,169,110,0.3)',
            color: colors.gold, cursor: 'pointer', fontWeight: 600, fontSize: 14,
            fontFamily: fonts.body, transition: 'all 0.2s',
          }}
        >
          🛒 Panier {cartCount > 0 && <span style={{ background: '#E74C3C', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11, marginLeft: 4 }}>{cartCount}</span>}
        </button>
      </div>

      {/* Cart Panel */}
      {showCart && cart.length > 0 && (
        <div style={{
          background: '#1A1A3A', borderRadius: radius.lg, padding: 20, marginBottom: 24,
          border: '1px solid #2A2A4A',
        }}>
          <h3 style={{ color: colors.gold, margin: '0 0 12px 0' }}>🛒 Votre panier</h3>
          {cart.map(c => (
            <div key={c.product.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #2A2A4A' }}>
              <div>
                <span style={{ color: '#F5F5F5', fontWeight: 600 }}>{c.product.name}</span>
                <span style={{ color: '#A0A0A0', marginLeft: 8 }}>x{c.qty}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: colors.gold, fontWeight: 700 }}>{c.product.price * c.qty}€</span>
                <button onClick={() => removeFromCart(c.product.id)} style={{ background: 'none', border: 'none', color: '#E74C3C', cursor: 'pointer', fontSize: 16 }}>✕</button>
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, paddingTop: 12, borderTop: `2px solid ${colors.gold}` }}>
            <span style={{ color: '#F5F5F5', fontWeight: 700, fontSize: 16 }}>Total</span>
            <span style={{ color: colors.gold, fontWeight: 700, fontSize: 18 }}>{cartTotal}€</span>
          </div>
          <button style={{
            width: '100%', marginTop: 16, padding: 12, borderRadius: radius.md,
            background: `linear-gradient(135deg, ${colors.gold}, #B8860B)`, border: 'none',
            color: '#0F0F23', fontWeight: 700, fontSize: 15, cursor: 'pointer',
            fontFamily: fonts.body,
          }}>
            Commander (Démo)
          </button>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 24 }}>
        {categories.map(c => (
          <button
            key={c.key}
            onClick={() => setFilter(c.key)}
            style={{
              padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer',
              background: filter === c.key ? colors.gold : 'rgba(201,169,110,0.15)',
              color: filter === c.key ? '#0F0F23' : colors.gold,
              fontWeight: filter === c.key ? 700 : 400, fontSize: 13,
              fontFamily: fonts.body, transition: 'all 0.2s',
            }}
          >
            {c.emoji} {c.label}
          </button>
        ))}
      </div>

      {/* Product Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 20 }}>
        {filtered.map(p => (
          <div
            key={p.id}
            style={{
              background: '#1A1A3A', borderRadius: radius.lg, overflow: 'hidden',
              border: '1px solid #2A2A4A', transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(201,169,110,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ position: 'relative', aspectRatio: '1', background: '#0F0F23' }}>
              <img
                src={`${process.env.PUBLIC_URL}/${p.image}`}
                alt={p.name}
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              {p.badge && (
                <span style={{
                  position: 'absolute', top: 10, left: 10,
                  background: colors.gold, color: '#0F0F23',
                  padding: '3px 10px', borderRadius: 12,
                  fontSize: 11, fontWeight: 700,
                }}>
                  {p.badge}
                </span>
              )}
            </div>
            <div style={{ padding: '14px 16px 16px' }}>
              <h3 style={{ color: '#F5F5F5', fontSize: 15, fontWeight: 700, margin: '0 0 4px' }}>{p.name}</h3>
              <p style={{ color: '#A0A0A0', fontSize: 12, margin: '0 0 12px', lineHeight: 1.4 }}>{p.description}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: colors.gold, fontSize: 20, fontWeight: 700 }}>{p.price}€</span>
                <button
                  onClick={() => addToCart(p)}
                  style={{
                    padding: '8px 16px', borderRadius: 20,
                    background: addedId === p.id ? '#27AE60' : `linear-gradient(135deg, ${colors.gold}, #B8860B)`,
                    border: 'none', color: addedId === p.id ? '#fff' : '#0F0F23',
                    fontWeight: 700, fontSize: 12, cursor: 'pointer',
                    fontFamily: fonts.body, transition: 'all 0.3s',
                  }}
                >
                  {addedId === p.id ? '✓ Ajouté !' : 'Ajouter au panier'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}